import { Text, ToggleButton } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import {
  calculateMissingCredential,
  mapPlacementDetailRequirementsToPlacementDataRequirements,
} from "@src/appV2/redesign/Documents/utils/calculateMissingCredential";
import { type RefObject, useEffect, useRef, useState } from "react";

import { type PlacementDetail } from "../../../api/useGetPlacement";
import { usePlacementRecruiterScreeningConfig } from "../../../hooks/usePlacementRecruiterScreeningConfig";
import {
  PLACEMENT_DETAILS_SECTION_IDS,
  PLACEMENT_DETAILS_SECTION_IDS_WITHOUT_REQUIREMENTS,
  PLACEMENT_DETAILS_SECTIONS,
  PLACEMENT_DETAILS_SECTIONS_WITHOUT_REQUIREMENTS,
  type PlacementDetailsSectionId,
} from "../../../placementDetailsSections";
import { shouldShowRequirement } from "../../../utils";
import { useActiveSectionChip } from "../hooks/useActiveSectionChip";
import { scrollToSectionInContainer } from "../sectionNavigation";

const MANUAL_SELECTION_TIMEOUT_MS = 1500;

interface SectionChipsProps {
  scrollContainerRef: RefObject<HTMLDivElement>;
  credentialRequirements: PlacementDetail["credentialRequirements"];
  workerRequirements: PlacementDetail["workerRequirements"];
  placementId: string;
  placementCandidateId: string;
  workerId: string;
}

export function SectionChips(props: SectionChipsProps) {
  const {
    scrollContainerRef,
    credentialRequirements,
    workerRequirements,
    placementId,
    placementCandidateId,
    workerId,
  } = props;

  const { legacyDocumentRequirementId: legacyScreeningDocumentRequirementId } =
    usePlacementRecruiterScreeningConfig();

  const hasCredentialRequirements = isDefined(
    calculateMissingCredential(
      mapPlacementDetailRequirementsToPlacementDataRequirements(credentialRequirements)
    )
  );

  const hasDocumentRequirements =
    workerRequirements?.some((requirement) =>
      shouldShowRequirement(requirement, legacyScreeningDocumentRequirementId)
    ) ?? false;

  const hasRequirements = hasCredentialRequirements || hasDocumentRequirements;

  const chipRowRef = useRef<HTMLDivElement>(null);
  const chipRefMap = useRef<Partial<Record<PlacementDetailsSectionId, HTMLDivElement | undefined>>>(
    {}
  );
  const manualSelectionTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [manuallySelectedSectionId, setManuallySelectedSectionId] = useState<
    PlacementDetailsSectionId | undefined
  >(undefined);

  const sections = hasRequirements
    ? PLACEMENT_DETAILS_SECTIONS
    : PLACEMENT_DETAILS_SECTIONS_WITHOUT_REQUIREMENTS;
  const sectionIds = hasRequirements
    ? PLACEMENT_DETAILS_SECTION_IDS
    : PLACEMENT_DETAILS_SECTION_IDS_WITHOUT_REQUIREMENTS;

  const activeSectionId = useActiveSectionChip(scrollContainerRef, sectionIds);
  const selectedSectionId = manuallySelectedSectionId ?? activeSectionId;

  const previousActiveSectionRef = useRef(activeSectionId);

  useEffect(() => {
    if (!isDefined(activeSectionId) || activeSectionId === previousActiveSectionRef.current) {
      return;
    }

    previousActiveSectionRef.current = activeSectionId;
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "section_chip_active_changed",
      section: activeSectionId,
      placementId,
      placementCandidateId,
      workerId,
    });
  }, [activeSectionId, placementId, placementCandidateId, workerId]);

  useEffect(() => {
    if (!isDefined(manuallySelectedSectionId) || manuallySelectedSectionId !== activeSectionId) {
      return;
    }

    clearManualSelection();
  }, [activeSectionId, manuallySelectedSectionId]);

  useEffect(() => {
    return () => {
      if (isDefined(manualSelectionTimeoutRef.current)) {
        clearTimeout(manualSelectionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const chipRow = chipRowRef.current;

    if (!isDefined(selectedSectionId)) {
      return;
    }

    const activeChip = chipRefMap.current[selectedSectionId];

    if (!chipRow || !activeChip) {
      return;
    }

    if (isChipOutOfView({ chipRow, activeChip })) {
      activeChip.scrollIntoView({
        behavior: isDefined(manuallySelectedSectionId) ? "smooth" : "auto",
        block: "nearest",
        inline: "center",
      });
    }
  }, [manuallySelectedSectionId, selectedSectionId]);

  function handleChipClick(sectionId: PlacementDetailsSectionId) {
    const scrollContainer = scrollContainerRef.current;

    if (
      !isDefined(scrollContainer) ||
      !scrollToSectionInContainer({ scrollContainer, sectionId })
    ) {
      return;
    }

    setManualSelection(sectionId);

    logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_ACTIONS, {
      action: "section_chip_clicked",
      section: sectionId,
      placementId,
      placementCandidateId,
      workerId,
    });
  }

  function setManualSelection(sectionId: PlacementDetailsSectionId) {
    if (isDefined(manualSelectionTimeoutRef.current)) {
      clearTimeout(manualSelectionTimeoutRef.current);
    }

    setManuallySelectedSectionId(sectionId);
    manualSelectionTimeoutRef.current = setTimeout(() => {
      manualSelectionTimeoutRef.current = undefined;
      setManuallySelectedSectionId(undefined);
    }, MANUAL_SELECTION_TIMEOUT_MS);
  }

  function clearManualSelection() {
    if (isDefined(manualSelectionTimeoutRef.current)) {
      clearTimeout(manualSelectionTimeoutRef.current);
      manualSelectionTimeoutRef.current = undefined;
    }

    setManuallySelectedSectionId(undefined);
  }

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        paddingX: 6,
      })}
    >
      <Stack
        ref={chipRowRef}
        direction="row"
        gap={2}
        sx={{
          overflowX: "auto",
          whiteSpace: "nowrap",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          paddingY: 1,
        }}
      >
        {sections.map((section) => (
          <Box
            key={section.id}
            ref={(chipElement: HTMLDivElement | null) => {
              chipRefMap.current[section.id] = chipElement ?? undefined;
            }}
          >
            <ToggleButton
              value={section.id}
              selected={selectedSectionId === section.id}
              size="small"
              onClick={() => {
                handleChipClick(section.id);
              }}
            >
              <Text semibold variant="body2">
                {section.label}
              </Text>
            </ToggleButton>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

interface IsChipOutOfViewParams {
  chipRow: HTMLDivElement;
  activeChip: HTMLDivElement;
}

function isChipOutOfView(params: IsChipOutOfViewParams) {
  const { chipRow, activeChip } = params;
  const visibleLeftEdge = chipRow.scrollLeft;
  const visibleRightEdge = visibleLeftEdge + chipRow.clientWidth;
  const chipLeftEdge = activeChip.offsetLeft;
  const chipRightEdge = chipLeftEdge + activeChip.offsetWidth;

  return chipLeftEdge < visibleLeftEdge || chipRightEdge > visibleRightEdge;
}
